
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Crown, CheckCircle, Clock, User, Building, Shield, Loader2 } from 'lucide-react';
import PremiumFeatures from '@/components/premium/PremiumFeatures';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { useSubscription } from '@/hooks/useSubscription';
import ProtectedRoute from '@/components/ProtectedRoute';
import { format } from 'date-fns';

const PremiumDashboard = () => {
  const navigate = useNavigate();
  const { isPremiumUser, user, isAdmin, adminLoading, premiumFeatures } = usePremiumAccess();
  const { subscription, isSubscribed } = useSubscription();
  const [selectedUserType, setSelectedUserType] = useState<'teen' | 'startup'>('teen');

  console.log('Premium Dashboard - User state:', { 
    user: user?.id, 
    isPremiumUser, 
    isAdmin, 
    adminLoading,
    subscription,
    isSubscribed,
    hasAccess: premiumFeatures.premiumDashboardAccess
  });

  // Redirect non-premium users to pricing page
  useEffect(() => {
    if (!adminLoading && !premiumFeatures.premiumDashboardAccess) {
      navigate('/pricing');
    }
  }, [adminLoading, premiumFeatures.premiumDashboardAccess, navigate]);

  const subscriptionEnd = subscription?.subscription_end ? new Date(subscription.subscription_end) : null;
  const daysRemaining = subscriptionEnd ? Math.ceil((subscriptionEnd.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  // Show admin access or premium access
  const hasAccess = premiumFeatures.premiumDashboardAccess;

  if (adminLoading) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-[#FF8C00]" />
                <p className="text-muted-foreground">Loading premium access...</p>
              </div>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  // If user doesn't have access, they will be redirected by useEffect
  if (!hasAccess) {
    return null;
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 hover:bg-orange-50 transition-all duration-300"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Crown className="h-8 w-8 text-[#FF8C00] animate-pulse" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#FF8C00] to-orange-500 bg-clip-text text-transparent">
                Premium Dashboard
              </h1>
              {isAdmin && (
                <Shield className="h-8 w-8 text-blue-500 animate-bounce" />
              )}
            </div>
            
            {isAdmin && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-blue-500" />
                <p className="text-xl text-blue-600 font-medium">
                  🛡️ Admin Access - All Premium Features Available
                </p>
              </div>
            )}
            
            {hasAccess ? (
              <div className="flex items-center justify-center gap-2 mb-4">
                <CheckCircle className="h-5 w-5 text-green-500 animate-pulse" />
                <p className="text-xl text-green-600 font-medium">
                  {isAdmin ? '🛡️ Admin Access Active' : '✨ Premium Active'} - Welcome back!
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Clock className="h-5 w-5 text-amber-500 animate-spin" />
                <p className="text-xl text-amber-600 font-medium">
                  Premium features locked - Upgrade to unlock
                </p>
              </div>
            )}
          </div>

          {/* Admin Status Card */}
          {isAdmin && (
            <Card className="mb-8 max-w-2xl mx-auto border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-500 animate-pulse" />
                  🛡️ Admin Access Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Access Level</p>
                    <Badge className="bg-blue-500 text-white mt-1 animate-pulse">
                      🛡️ Administrator
                    </Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Premium Features</p>
                    <Badge className="bg-green-500 text-white mt-1 animate-bounce">
                      ✨ All Unlocked
                    </Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Admin Panel</p>
                    <Button 
                      size="sm" 
                      onClick={() => navigate('/admin')}
                      className="bg-blue-500 hover:bg-blue-600 mt-1 transition-all duration-300 hover:scale-105"
                    >
                      🔧 Access Admin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subscription Status Card */}
          {subscription && !isAdmin && (
            <Card className="mb-8 max-w-2xl mx-auto shadow-lg border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-[#FF8C00] animate-pulse" />
                  ✨ Subscription Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Plan</p>
                    <Badge className="bg-[#FF8C00] text-white mt-1">
                      {subscription.subscription_tier} Premium
                    </Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={isSubscribed ? 'default' : 'destructive'} className="mt-1">
                      {isSubscribed ? 'Active' : 'Expired'}
                    </Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Expires</p>
                    <p className="text-sm font-medium mt-1">
                      {subscriptionEnd ? format(subscriptionEnd, 'MMM dd, yyyy') : 'N/A'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Days Left</p>
                    <p className={`text-sm font-medium mt-1 ${daysRemaining <= 7 ? 'text-red-500' : 'text-green-500'}`}>
                      {daysRemaining > 0 ? daysRemaining : 'Expired'}
                    </p>
                  </div>
                </div>
                
                {daysRemaining <= 7 && daysRemaining > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700 text-center">
                      ⚠️ Your subscription expires soon! Consider renewing to keep your premium access.
                    </p>
                    <div className="flex justify-center mt-2">
                      <Button size="sm" onClick={() => navigate('/pricing')} className="bg-[#FF8C00] hover:bg-orange-600">
                        Renew Subscription
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* User Type Selector */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-2 shadow-lg border border-orange-200">
              <div className="flex gap-2">
                <Button
                  variant={selectedUserType === 'teen' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedUserType('teen')}
                  className={selectedUserType === 'teen' ? 'bg-gradient-to-r from-[#FF8C00] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white animate-pulse' : 'hover:bg-orange-50'}
                >
                  <User className="h-4 w-4 mr-2" />
                  👨‍🎓 Teen Features
                </Button>
                <Button
                  variant={selectedUserType === 'startup' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedUserType('startup')}
                  className={selectedUserType === 'startup' ? 'bg-gradient-to-r from-[#FF8C00] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white animate-pulse' : 'hover:bg-orange-50'}
                >
                  <Building className="h-4 w-4 mr-2" />
                  🚀 Startup Features
                </Button>
              </div>
            </div>
          </div>

          {/* Premium Features */}
          <PremiumFeatures />

          {/* Quick Actions for Premium/Admin Users */}
          {hasAccess && (
            <div className="mt-12 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-[#FF8C00] to-orange-600 bg-clip-text text-transparent">
                ⚡ Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 border-orange-200" onClick={() => navigate('/dashboard')}>
                  <CardContent className="p-6 text-center">
                    <User className="h-8 w-8 mx-auto mb-3 text-[#FF8C00] animate-pulse" />
                    <h4 className="font-medium">📊 Main Dashboard</h4>
                    <p className="text-sm text-muted-foreground">Access your main dashboard</p>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 border-orange-200" onClick={() => navigate('/profile')}>
                  <CardContent className="p-6 text-center">
                    <User className="h-8 w-8 mx-auto mb-3 text-[#FF8C00] animate-pulse" />
                    <h4 className="font-medium">👤 Profile Settings</h4>
                    <p className="text-sm text-muted-foreground">Manage your profile</p>
                  </CardContent>
                </Card>
                
                {isAdmin ? (
                  <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 border-blue-200" onClick={() => navigate('/admin')}>
                    <CardContent className="p-6 text-center">
                      <Shield className="h-8 w-8 mx-auto mb-3 text-blue-500 animate-bounce" />
                      <h4 className="font-medium">🛡️ Admin Panel</h4>
                      <p className="text-sm text-muted-foreground">Manage platform settings</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 border-orange-200" onClick={() => navigate('/opportunities')}>
                    <CardContent className="p-6 text-center">
                      <Building className="h-8 w-8 mx-auto mb-3 text-[#FF8C00] animate-pulse" />
                      <h4 className="font-medium">🎯 Opportunities</h4>
                      <p className="text-sm text-muted-foreground">Explore premium opportunities</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default PremiumDashboard;
